import { API_Data_Survey_Content_Response, API_Data_Exit_Survey_Response } from "@/interfaces/api/survey/api_response_types"
import { API_Error, API_Response, API_Success_Response } from "@/interfaces/api/response/api_response_types"
import { SubmitSurveyPayload } from "@/interfaces/api/survey/api_request_types"
import { fetcher } from "@/utils/helpers"

/**
 * Fetches the full survey content from a given URL
 * 
 * @param url Endpoint URL to retrieve survey content
 * @returns Survey content data
 * @throws API_Error if the request fails
 */
export async function getSurveyContent (url: string): Promise<API_Data_Survey_Content_Response> {
  const res = await fetcher(`${url}`, { 
    method: 'GET',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
    }
  })

  const response = await res.json() as API_Data_Survey_Content_Response
  
  if (!res.ok) throw { code: res.status, description: 'Unknow survey content error' } as API_Error
  
  return response
}

/**
 * Fetches the availability and the URL to retrieve the exit survey metadata for a given locale
 * 
 * @param locale Language code to request the survey
 * @returns Exit survey response data
 * @throws API_Error if the request fails or response is invalid
 */
export async function checkExitSurvey (locale: string): Promise<API_Data_Exit_Survey_Response> {
  const res = await fetcher(`/api/account-deletion-survey?lang=${locale}`, { 
    method: 'GET',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
    }
  })

  const response = await res.json() as API_Response<API_Data_Exit_Survey_Response>
  
  if (!res.ok) throw { code: res.status, description: 'Unknow check exit survey error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Check exit survey data missing in response' } as API_Error
  
  return response.data
}


/**
 * Submits the exit survey payload to the backend
 * 
 * @param payload Exit survey data to submit
 * @returns Success response from the API
 * @throws API_Error if the request fails or response indicates an error
 */
export async function submitExitSurvey (payload: SubmitSurveyPayload): Promise<API_Success_Response<undefined>> {
  const res = await fetcher("/api/account-deletion-survey", { 
    method: 'POST',
    cache: "no-store",
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  })
  const response = await res.json() as API_Response<undefined>
  
  if (!res.ok) throw { code: res.status, description: 'Unknow submit exit survey error' } as API_Error
  if (response.status === 'error') throw response.error

  return response
}